<script setup>
import { ref, computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { dispatchWithAlert } from 'dashboard/helper/dispatchWithAlert';
import { useAlert } from 'dashboard/composables';

import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import ResolveWithSummaryModal from 'dashboard/components-next/ConversationWorkflow/ResolveWithSummaryModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const currentUser = computed(() => getters.getCurrentUser.value);
const { checkMissingAttributes } = useConversationRequiredAttributes();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);
const resolveSummaryModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const alternarMenu = () => toggleDropdown();
// A seta fica fora do menu: sem o `ignore`, clicar nela com o menu aberto
// fechava pelo clique-fora e reabria pelo próprio clique.
const cliqueFora = [closeDropdown, { ignore: [arrowDownButtonRef] }];

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  closeDropdown();
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (
  status,
  snoozedUntil,
  customAttributes = null,
  assumir = false
) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
    assumir,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  // `finally`: antes o `isLoading` só era desligado no sucesso — falha da API
  // deixava o botão preso em "carregando" pra sempre.
  return dispatchWithAlert(store.dispatch('toggleStatus', payload), {
    success: t('CONVERSATION.CHANGE_STATUS'),
    failure: t('CONVERSATION.CHANGE_STATUS_FAILED'),
  }).finally(() => {
    isLoading.value = false;
  });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

// 🔴 O ÚNICO LUGAR QUE PEDE A CONVERSA PARA SI.
//
// Apertar "Reabrir" é dizer "eu assumo". Todo o resto que reabre uma conversa
// — cliente que volta a escrever, automação, entrega do bot — reabre sem
// dono, e ela vai para a fila. Regra do Victor (19/09/2026), depois de três
// conversas caírem no colo dele sem ele pedir.
const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN, null, null, true);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    resolveSummaryModalRef.value?.open();
  }
};

const handleResolveSummaryConfirm = async ({ summary }) => {
  await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  if (summary && summary.trim()) {
    // A conversa já foi resolvida; se a nota do resumo não for, a pessoa
    // precisa saber — senão acha que o resumo ficou registrado.
    store
      .dispatch('createPendingMessageAndSend', {
        conversationId: currentChat.value.id,
        message: summary.trim(),
        private: true,
        sender: {
          name: currentUser.value.name,
          thumbnail: currentUser.value.avatar_url,
        },
      })
      .catch(() => useAlert(t('CONVERSATION.RESOLVE_SUMMARY_FAILED')));
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <!-- A AÇÃO PRINCIPAL DA CONVERSA. Era o botão cinza do Chatwoot: a coisa
       que encerra o atendimento com o mesmo peso de "Cancelar". Agora é o
       botão dividido do produto — teal para concluir, azul para reabrir —
       com as outras saídas (adiar, pendente) num menu da peça. -->
  <div class="rsv">
    <div class="rsv__grupo" :class="{ 'rsv__grupo--azul': !isOpen }">
      <button
        v-if="isOpen"
        type="button"
        class="rsv__principal"
        :disabled="isLoading"
        @click="onCmdResolveConversation"
      >
        <i
          :class="
            isLoading ? 'i-lucide-loader-2 advup-gira' : 'i-lucide-check-check'
          "
          aria-hidden="true"
        />
        {{ t('CONVERSATION.HEADER.RESOLVE_ACTION') }}
      </button>
      <button
        v-else-if="isResolved"
        type="button"
        class="rsv__principal"
        :disabled="isLoading"
        @click="onCmdOpenConversation"
      >
        <i
          :class="
            isLoading ? 'i-lucide-loader-2 advup-gira' : 'i-lucide-rotate-ccw'
          "
          aria-hidden="true"
        />
        {{ t('CONVERSATION.HEADER.REOPEN_ACTION') }}
      </button>
      <button
        v-else-if="showOpenButton"
        type="button"
        class="rsv__principal"
        :disabled="isLoading"
        @click="onCmdOpenConversation"
      >
        <i
          :class="
            isLoading
              ? 'i-lucide-loader-2 advup-gira'
              : 'i-lucide-message-circle'
          "
          aria-hidden="true"
        />
        {{ t('CONVERSATION.HEADER.OPEN_ACTION') }}
      </button>
      <button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        type="button"
        class="rsv__seta"
        :class="{ 'rsv__seta--aberta': showActionsDropdown }"
        :disabled="isLoading"
        :aria-label="t('CONVERSATION.RESOLVE_DROPDOWN.MORE')"
        :aria-expanded="showActionsDropdown"
        aria-haspopup="menu"
        @click="alternarMenu"
      >
        <i class="i-lucide-chevron-down" />
      </button>
    </div>

    <Transition name="advup-pop">
      <div
        v-if="showActionsDropdown"
        v-on-click-outside="cliqueFora"
        class="advup-menu advup-pop rsv__menu"
        role="menu"
        @keydown.esc="closeDropdown"
      >
        <button
          v-if="!isPending"
          type="button"
          role="menuitem"
          class="advup-menu__item"
          @click="openSnoozeModal()"
        >
          <i
            class="i-lucide-alarm-clock rsv__ico rsv__ico--ambar"
            aria-hidden="true"
          />
          {{ t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL') }}
        </button>
        <button
          v-if="!isPending"
          type="button"
          role="menuitem"
          class="advup-menu__item"
          @click="toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
        >
          <i
            class="i-lucide-circle-dot-dashed rsv__ico rsv__ico--iris"
            aria-hidden="true"
          />
          {{ t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING') }}
        </button>
      </div>
    </Transition>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <ResolveWithSummaryModal
      ref="resolveSummaryModalRef"
      @confirm="handleResolveSummaryConfirm"
    />
  </div>
</template>

<style scoped>
.rsv {
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
}

.rsv__grupo {
  --rsv-a: var(--teal-9);
  --rsv-b: var(--teal-10);

  display: flex;
  overflow: hidden;
  border-radius: var(--r-3);
  background: linear-gradient(
    150deg,
    rgb(var(--rsv-a)) 0%,
    rgb(var(--rsv-b)) 120%
  );
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 22%),
    0 4px 12px -6px rgb(var(--rsv-a) / 70%);
  transition:
    box-shadow var(--mov-2) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva-pulo);
}

.rsv__grupo:hover {
  translate: 0 -1px;
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 22%),
    0 8px 18px -8px rgb(var(--rsv-a) / 80%);
}

.rsv__grupo--azul {
  --rsv-a: var(--blue-9);
  --rsv-b: var(--iris-9);
}

.rsv__principal,
.rsv__seta {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2);
  height: 32px;
  border: 0;
  background: transparent;
  color: rgb(255 255 255);
  cursor: pointer;
  transition: background-color var(--mov-1) var(--mov-curva);
}

.rsv__principal {
  padding: 0 var(--esp-3);
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
}

.rsv__principal > i,
.rsv__seta > i {
  width: var(--ico-2);
  height: var(--ico-2);
}

.rsv__seta {
  justify-content: center;
  width: 30px;
  border-inline-start: 1px solid rgb(255 255 255 / 25%);
}

.rsv__seta > i {
  transition: rotate var(--mov-2) var(--mov-curva);
}

.rsv__seta--aberta > i {
  rotate: 180deg;
}

.rsv__principal:hover:not(:disabled),
.rsv__seta:hover:not(:disabled),
.rsv__seta--aberta {
  background: rgb(255 255 255 / 14%);
}

.rsv__principal:disabled,
.rsv__seta:disabled {
  cursor: progress;
  opacity: 0.85;
}

.rsv__principal:focus-visible,
.rsv__seta:focus-visible {
  outline: 2px solid rgb(255 255 255 / 80%);
  outline-offset: -3px;
}

.rsv__menu {
  position: absolute;
  z-index: 30;
  top: calc(100% + var(--esp-1));
  inset-inline-end: 0;
  width: max-content;
}

.rsv__ico {
  flex-shrink: 0;
  font-size: var(--ico-2);
}

.rsv__ico--ambar {
  color: rgb(var(--amber-11));
}

.rsv__ico--iris {
  color: rgb(var(--iris-11));
}

@media (max-width: 1279px) {
  .rsv__menu {
    inset-inline-start: 0;
    inset-inline-end: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .rsv__grupo,
  .rsv__seta > i {
    transition: none;
    translate: none;
  }
}
</style>
